import { usecaseAssets } from "./assets";

export type UseCaseId =
  | "pcos"
  | "trying-after-35"
  | "after-miscarriage"
  | "irregular-cycle"
  | "before-ivf";

export interface UseCaseTabItem {
  id: UseCaseId;
  label: string;
}

export type UseCasePointVisual =
  | {
      type: "image";
      src: string;
      alt: string;
      fit?: "contain" | "cover";
      unoptimized?: boolean;
    }
  | {
      type: "frames";
      frames: readonly string[];
      alt: string;
      intervalMs?: number;
    };

export interface UseCasePoint {
  title: string;
  description: string;
  subtext?: string;
  visual: UseCasePointVisual;
}

export interface UseCaseContent {
  hero: {
    title: string;
    description: string;
    source?: string;
  };
  valuePropHeader: string;
  points: [UseCasePoint, UseCasePoint, UseCasePoint];
  expertQuote: {
    quote: string;
    name: string;
    title: string;
    socialHandle?: string;
    /** Doctor photo; omit to use default (MDJ) image */
    image?: string;
    /** CSS object-position for the doctor image (desktop only) */
    imagePosition?: string;
    /** Optional mobile background treatment for doctor quote card */
    mobileBackgroundVariant?:
      | "default"
      | "pcos"
      | "trying-after-35"
      | "after-miscarriage"
      | "irregular-cycle"
      | "before-ivf";
    /** Mobile doctor-quote background image only; desktop uses `image` */
    mobileImage?: string;
  };
}

export const USE_CASE_TABS: UseCaseTabItem[] = [
  { id: "pcos", label: "PCOS" },
  { id: "trying-after-35", label: "Trying after 35" },
  { id: "after-miscarriage", label: "After miscarriage" },
  { id: "irregular-cycle", label: "Irregular cycle" },
  { id: "before-ivf", label: "Before IVF" },
];

export const USE_CASE_ROUTE_ORDER: readonly UseCaseId[] = [
  "pcos",
  "trying-after-35",
  "after-miscarriage",
  "irregular-cycle",
  "before-ivf",
];

export const USE_CASE_ROUTE_SEGMENTS: Record<
  UseCaseId,
  "1" | "2" | "3" | "4" | "5"
> = {
  pcos: "1",
  "trying-after-35": "2",
  "after-miscarriage": "3",
  "irregular-cycle": "4",
  "before-ivf": "5",
};

/** Prefix for page analytics, e.g. `usecase_pcos_page_loaded`, `usecase_trying_after_35_page_scrolled`. */
export function getUseCasePageEventPrefix(useCaseId: UseCaseId): string {
  return `usecase_${useCaseId.replace(/-/g, "_")}`;
}

export const USE_CASE_CONTENT: Record<UseCaseId, UseCaseContent> = {
  pcos: {
    hero: {
      title: "8/10 women with PCOS don't ovulate regularly*",
      description:
        "Inito confirms ovulation and gives you the right days to try even with unpredictable cycles.",
      source: "Source: NCBI",
    },
    valuePropHeader: "How does Inito work with PCOS?",
    points: [
      {
        title: "See if you're actually ovulating after making lifestyle changes",
        description:
          "Inito looks for a sustained PdG (urine metabolite of progesterone) rise after an LH surge – the key biological confirmation that ovulation truly occurred so you know if your lifestyle changes are actually helping you ovulate.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.pcosValue1,
          alt: "Inito app showing ovulation after lifestyle changes",
        },
      },
      {
        title: "Know your right days to try, even with elevated LH",
        description:
          "Unlike OPKs that often trigger false positives due to elevated LH, Inito adapts to your unique hormone baseline and identifies your right days to try even with PCOS.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.pcosValue2Frames,
          alt: "Inito app identifying fertile days with PCOS",
        },
      },
      {
        title: "Have informed conversations with your doctor",
        description:
          "Inito builds a continuous hormone record so you and your doctor can track if treatments and supplements are helping you ovulate.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.pcosValue3,
          alt: "Inito app showing hormone history for doctor discussions",
        },
      },
    ],
    expertQuote: {
      quote:
        "“Today itself, I got a text message from a patient who said “This is the first time in the 4 years that I ever got a positive test!”\n\nI checked in with her every single day about her Inito results. I gave her encouragement as well as real time changes and pivots based on the day’s results.”",
      name: "Dr. Roxanne Pero",
      title: "Functional Med OB/GYN",
      socialHandle: "@drroxannepero",
      image: usecaseAssets.doctors.expertPcos,
      mobileBackgroundVariant: "pcos",
      mobileImage: usecaseAssets.doctors.expertPcosMobile,
    },
  },
  "trying-after-35": {
    hero: {
      title: "When every cycle counts, clarity isn't optional",
      description:
        "Inito shows you the right days to try and confirms ovulation, so you never miss your fertile window.",
    },
    valuePropHeader: "How does Inito work if you're trying after 35?",
    points: [
      {
        title: "Never miss an egg again",
        description:
          "After 35, there are rising fears of low AMH reducing egg quality and quantity. But it only takes 1 good egg to get pregnant - it is possible. Inito tracks estrogen, LH, and FSH to pinpoint your six-day fertile window and gives you peak fertility alerts even with irregular cycles, late ovulation, and smaller rises in hormone levels.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.ttc35Value1Frames,
          alt: "Inito app showing fertile window and peak fertility when trying after 35",
        },
      },
      {
        title: "Spot issues early",
        description:
          "Inito builds a clear cycle-by-cycle hormone record helping spot issues like short luteal phase and anovulation so you can act sooner rather than later.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.ttc35Value2Exact,
          alt: "Inito app confirming ovulation with PdG rise",
          fit: "cover",
          unoptimized: true,
        },
      },
      {
        title:
          "Confirm if you're still ovulating, before considering fertility treatments.",
        description:
          "Inito looks for a sustained PdG (urine metabolite of progesterone) rise after an LH surge – the key biological confirmation that ovulation truly occurred, so you have all the information to make the right decision before moving to advanced fertility treatments.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.ttc35Value2Frames,
          alt: "Inito app showing cycle-by-cycle hormone trends",
        },
      },
    ],
    expertQuote: {
      quote:
        "“(Inito) goes beyond prediction and actually provides confirmation. I recommend any woman over the age of 35 wanting to conceive in the near future to start tracking. This allows women to understand their fertility window or receive fertility treatment sooner if ovulation is not occurring.”",
      name: "Dr. Hailey Puckett",
      title: "Women’s Health Nurse Practitioner",
      socialHandle: "@HaileyPtheNP",
      image: usecaseAssets.doctors.expertTtc35,
      imagePosition: "center 30%",
      mobileBackgroundVariant: "trying-after-35",
      mobileImage: usecaseAssets.doctors.expertTtc35MobileExact,
    },
  },
  "after-miscarriage": {
    hero: {
      title: "Hormones take varying amounts of time to regularize for everyone",
      description:
        "Inito tracks PdG (urine metabolite of progesterone) to show when ovulation resumes in your cycle, giving you personalized clarity and not generic fertile windows on when your body is ready to try again.",
    },
    valuePropHeader: "How does Inito help if you're trying after a loss?",
    points: [
      {
        title: "Know when ovulation returns after a loss",
        description:
          "Inito tracks PdG rises so you know your ovulation has resumed. And if you're not yet ovulating, Inito will show that clearly too. Giving you a biological indicator of when your body is ready to try for your rainbow baby.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.ttc35Value2Frames,
          alt: "Inito app tracking ovulation and fertile window after a loss",
        },
      },
      {
        title: "Get a personal view of your recovery*",
        description:
          "Your cycle may not feel the way it used to. Watch hormone trends over time for a full picture of what is going on, and see your follicular and luteal phase clearly.",
        subtext:
          "*Inito cannot be used to predict, identify, track, or confirm a miscarriage. Knowing your luteal phase length helps you understand your cycle better. Please consult your doctor for more details.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.postMiscarriageValue2,
          alt: "Inito app showing hormone trends and cycle phases during recovery",
        },
      },
      {
        title: "Bring clarity to medical conversations",
        description:
          "Inito provides detailed hormone data you can share with your healthcare provider. This makes medical conversations more productive and helps guide discussions around reducing risk in future attempts.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.postMiscarriageValue3,
          alt: "Inito app with hormone data to share with your doctor",
        },
      },
    ],
    expertQuote: {
      quote:
        "“After a loss, patients often want reassurance that their bodies aren’t “broken”. Confirming ovulation and hormonal health provides both data and emotional reassurance. I recommend (Inito) because it closes information gaps. It’s like having a lab in your home!”",
      name: "Dr. Cyntia Onuoha-Brown",
      title: "PharmD, Integrative Fertility Expert",
      socialHandle: "@dr.cyntiabrown",
      image: usecaseAssets.doctors.expertPostMiscarriage,
      imagePosition: "center 30%",
      mobileBackgroundVariant: "after-miscarriage",
      mobileImage: usecaseAssets.doctors.expertPostMiscarriageMobile,
    },
  },
  "irregular-cycle": {
    hero: {
      title: "With unpredictable cycles, ovulation tests can fall short",
      description:
        "Inito gives you the right days to try so that you're not left guessing.",
    },
    valuePropHeader: "How does Inito work if you have irregular cycles?",
    points: [
      {
        title: "Time intercourse accurately",
        description:
          "Ovulation might be early, late, or not happen with erratic cycle lengths, making it easy to miss your fertile window. Inito tracks LH, estrogen, and FSH to pinpoint the right fertile days, no matter what your hormones do.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.ircValue1Frames,
          alt: "Inito app pinpointing fertile days with irregular cycles",
        },
      },
      {
        title: "Bleeding isn't proof of ovulation.",
        description:
          "With irregular cycles, ovulation may or may not happen. And just getting your period every cycle is not a sure sign that ovulation is actually happening. Inito measures PdG to confirm ovulation, so you know whether your body is truly fertile and can plan with confidence.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.ircValue2,
          alt: "Inito app confirming ovulation with PdG for irregular cycles",
        },
      },
      {
        title: "Advocate for yourself with data",
        description:
          "Irregular cycles can sometimes signal a hormonal imbalance that affects your ability to conceive. Inito tracks your hormone patterns cycle on cycle – like identifying a short luteal phase or ovulating late. So you can bring clear, personalized data to your doctor for more informed, productive conversations.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.ircValue3,
          alt: "Inito app showing hormone patterns to share with your doctor",
        },
      },
    ],
    expertQuote: {
      quote:
        "“If the issue is reproductive hormonal imbalance, Inito can be very helpful. It allows to better understand their cycle and time intercourse appropriately. Having this detailed hormonal history allows us as REI physicians to make more informed decisions and personalize treatment more effectively.”",
      name: "Dr. Susan Nasab",
      title: "OB/GYN & REI",
      socialHandle: "@susannasab.md",
      image: usecaseAssets.doctors.expertIrc,
      imagePosition: "center 30%",
      mobileBackgroundVariant: "irregular-cycle",
      mobileImage: usecaseAssets.doctors.expertIrcMobile,
    },
  },
  "before-ivf": {
    hero: {
      title: "Give yourself the best chance to try naturally before IVF",
      description:
        "Inito gives you clear, cycle-specific hormone data to determine whether to keep trying or take the next major step.",
    },
    valuePropHeader: "How does Inito help if you're considering IVF?",
    points: [
      {
        title: "Get your timing right for another try",
        description:
          "Inito identifies your right days to try even with unpredictable cycles, hormone fluctuations, and late ovulation – all backed by your actual hormone values of Estrogen, LH, PdG, and FSH. This gives you the best chance to try, before having to think about complex treatments.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.preIvfValue2Frames,
          alt: "Inito app identifying fertile days before considering IVF",
        },
      },
      {
        title: "Know with actual data if you are ovulating.",
        description:
          "Inito tracks your PdG (urine metabolite of progesterone) rise after an LH surge to confirm ovulation, showing whether your body is ovulating naturally to continue trying. Or whether you need to proceed with simple assistance to ovulate before considering IVF.",
        visual: {
          type: "frames",
          frames: usecaseAssets.howInitoHelps.ttc35Value2Frames,
          alt: "Inito app confirming ovulation with PdG before IVF",
        },
      },
      {
        title: "Decide with confidence",
        description:
          "Multiple cycles tracked means a full picture of your personalized patterns. So you can talk to IVF specialists being fully informed.",
        visual: {
          type: "image",
          src: usecaseAssets.howInitoHelps.preIvfValue3,
          alt: "Inito app showing multi-cycle hormone patterns",
        },
      },
    ],
    expertQuote: {
      quote:
        "“(Inito) helps confirm ovulation and spot patterns over time, which can be empowering for people trying to conceive. For many, the biggest benefit is clarity and feeling more in control of their journey. More information leads to better conversations with their care team”",
      name: "Alease Barnes",
      title: "Embryologist",
      socialHandle: "@alease_the_embryologist",
      image: usecaseAssets.doctors.expertPreIvf,
      imagePosition: "center 30%",
      mobileBackgroundVariant: "before-ivf",
      mobileImage: usecaseAssets.doctors.expertPreIvfMobile,
    },
  },
};

export function getUseCaseHref(useCaseId: UseCaseId): string {
  return `/how-inito-works/${USE_CASE_ROUTE_SEGMENTS[useCaseId]}`;
}

export function getUseCaseIdFromSegment(segment: string): UseCaseId | undefined {
  return USE_CASE_ROUTE_ORDER.find(
    (useCaseId) => USE_CASE_ROUTE_SEGMENTS[useCaseId] === segment,
  );
}

const USE_CASE_CARD_DESCRIPTIONS: Record<UseCaseId, string> = {
  pcos: "See if lifestyle changes are helping you ovulate with PCOS",
  "trying-after-35": "Make every cycle count when trying after 35",
  "after-miscarriage": "Know when your body is ready to try again after a loss",
  "irregular-cycle":
    "Pinpoint your right days to try, even with unpredictable cycles",
  "before-ivf":
    "Give yourself the best chance to try naturally before considering IVF",
};

export const USE_CASES_NAV_ITEMS = USE_CASE_TABS.map((tab) => ({
  label: tab.label,
  href: getUseCaseHref(tab.id),
  description: USE_CASE_CARD_DESCRIPTIONS[tab.id],
}));
